import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useEnrollmentFormData } from './StudentEnrollmentFormContext';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 40px 32px;
  background: #fff;
  border-radius: 15px;  
  font-family: 'sans-serif';
`;

const BlueHeader = styled.div`
  background: #1e3a8a;
  color: white;
  padding: 20px;
  margin: -40px -32px 30px -32px;
  border-radius: 15px 15px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
`;

const LogoLetter = styled.div`
  width: 60px;
  height: 60px;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  position: relative;
  
  &::after {
    content: attr(data-text);
    position: absolute;
    bottom: -15px;
    font-size: 8px;
    font-weight: normal;
    white-space: nowrap;
  }
`;

const FormTitle = styled.h1`
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  text-decoration: underline;
  margin: 30px 0;
  color: #2c3e50;
`;

const FormContent = styled.div`
  font-family: sans-serif;
  line-height: 1.6;
  color: #333;
`;

const IntroParagraph = styled.div`
  margin: 20px 0;
  font-size: 1rem;
  line-height: 1.8;
  text-align: left;
`;

const NameSection = styled.div`
  margin: 30px 0;
  font-size: 1rem;
  line-height: 1.8;
`;

const Blank = styled.span`
  display: inline-block;
  min-width: 200px;
  border-bottom: 1px solid #333;
  margin: 0 4px;
  padding: 0 4px;
  font-weight: 500;
`;

const CheckboxGroup = styled.div`
  margin: 20px 0;
  padding-left: 20px;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  margin: 15px 0;
  
  input[type="checkbox"] {
    margin-right: 12px;
    transform: scale(1.2);
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 18px;
    height: 18px;
    border: 1px solid #333;
    border-radius: 3px;
    background-color: white;
    cursor: pointer;
    position: relative;
  }
  
  input[type="checkbox"]:checked {
    background-color: #000;
    border-color: #000;
  }
  
  input[type="checkbox"]:checked::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 14px;
    font-weight: bold;
  }
  
  label {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const SignatureSection = styled.div`
  margin: 40px 0;
`;

const SignatureRow = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 0 0;
  font-size: 1rem;
`;

const SigLabel = styled.span`
  color: #222;
`;

const SigValue = styled.span`
  border-bottom: 1px solid #333;
  margin: 0 8px;
  padding: 0 4px;
  font-weight: 500;
  min-width: 300px;
  display: inline-block;
`;

const SignatureImage = styled.img`
  max-height: 40px;
  max-width: 200px;
  object-fit: contain;
  border: none;
  background: transparent;
  border-bottom: 1px solid #333;
  margin-bottom: 18px;
`;

const DateValue = styled.span`
  border-bottom: 1px solid #333;
  margin: 0 8px;
  padding: 0 4px;
  font-weight: 500;
  min-width: 150px;
  display: inline-block;
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 24px auto;
  max-width: 600px;
`;

const DownloadButton = styled.button`
  background: #334293;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 32px auto 0 auto;
  display: block;

  &:hover {
    background: #2c3e50;
    transform: translateY(-2px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6c757d;
  font-size: 1.1rem;
  margin: 40px 0;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  border: 1px solid #f5c6cb;
`;

const PrintHideStyle = createGlobalStyle`
  @media print {
    .print-hide {
      display: none !important;
    }
    
    @page {
      margin: 0;
      size: A4;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const CovidVaccinationAttestationForm = () => {
  const { email } = useParams();
  const { data: student, loading, error } = useEnrollmentFormData();
  const docRef = useRef();

  const handleDownload = () => {
    window.print();
  };

  if (loading) {
    return <Container><LoadingMessage>Loading student information...</LoadingMessage></Container>;
  }
  if (error || !student) {
    return <Container><ErrorMessage>{error || 'Student not found'}</ErrorMessage></Container>;
  }

  const today = new Date();
  const dateStr = today.toLocaleDateString();

  // Get COVID-19 vaccination status from enrollment form data
  // Check both direct field and form data field
  const covidVaccineReceived = student.covid_vaccine_received === 1 ||
    student.covid_vaccine_received === true ||
    student.covid_vaccine_received === 'true';

  return (
    <Container>
      <PrintHideStyle />
      <div ref={docRef} style={{ background: '#fff', color: '#222', padding: 0 }}>

        <Logo src={process.env.PUBLIC_URL + '/forms_logo.png'} alt="CCLC Logo" />

        <p style={{ textAlign: 'center', fontFamily: 'sans-serif', fontSize: '13px',fontWeight: 'bold'}}>
          <i>
            Chicago Campus: 840 W. Irving Park Road, suit 203, Illinois 60613 (773) 506-1503 Main Telephone
          </i>
        </p>
        <p style={{ textAlign: 'center', fontFamily: 'sans-serif', fontSize: '13px',fontWeight: 'bold'}}>
          <i>
            Evergreen Campus: 9730 S. Western Ave, suit 502, Evergreen Park, Illinois 60805 (708) 576-8990 Main office
          </i>
        </p>
        <div style={{textAlign: 'center'}}>
        <a href="https://www.cclctraining.org/" target='_blank' style={{  fontFamily: 'sans-serif', fontSize: '13px',fontWeight: 'bold',color:"#0083b9"}} >www.cclctraining.org</a>
        </div>
        <FormTitle>PROSPECT STUDENTS ATTESTATION OF COVID-19 VACCINATION</FormTitle>

        <FormContent>
          <IntroParagraph>
            As part of our efforts to provide a safe educational environment, Chicago Community Learning Center, Inc is tracking the number of Prospect Student who Have or Have Not received the COVID-19 vaccine. Please complete this form to let us know whether and when you have received the vaccine. We are asking for dates of the vaccination to help determine when each Prospect Student is considered fully vaccinated or Not. When completing this form DO NOT provide any medical information, or any other information related to why you may or may not have received a COVID-19 vaccine. Simply fill in your name, check the appropriate box, sign, and date.
          </IntroParagraph>

          <NameSection>
            My name is <Blank>{student.firstName} {student.lastName}</Blank> and I attest that (check only one):
          </NameSection>

          <CheckboxGroup>
            <CheckboxItem>
              <input
                type="checkbox"
                id="vaccine-received"
                name="vaccine_status"
                value="received"
                disabled
                checked={covidVaccineReceived}
              />
              <label htmlFor="vaccine-received">I received the complete series of the COVID-19 vaccine.</label>
            </CheckboxItem>

            <CheckboxItem>
              <input
                type="checkbox"
                id="vaccine-not-received"
                name="vaccine_status"
                value="not-received"
                disabled
                checked={!covidVaccineReceived}
              />
              <label htmlFor="vaccine-not-received">I Have Not received a COVID-19 vaccine (please DO NOT provide any additional information)</label>
            </CheckboxItem>
          </CheckboxGroup>

          <SignatureSection>
            <SignatureRow>
              <SigLabel>Signature:</SigLabel>
              {(() => {
                const sigData = student.student_ack_signature_data;

                // Check if it's a valid data URL (must have content after data:)
                if (typeof sigData === 'string' && sigData.startsWith('data:') && sigData.length > 6) {
                  return (
                    <SignatureImage src={sigData} alt="Student Signature" />
                  );
                }

                // Check if it's SVG markup
                if (typeof sigData === 'string' && sigData.trim().startsWith('<svg')) {
                  return (
                    <div
                      dangerouslySetInnerHTML={{ __html: sigData }}
                      style={{ maxHeight: 40, maxWidth: 200 }}
                    />
                  );
                }

                // Check if it's a base64 string (without data URL prefix)
                if (typeof sigData === 'string' && sigData.length > 100 && /^[A-Za-z0-9+/=]+$/.test(sigData)) {
                  return (
                    <SignatureImage
                      src={`data:image/png;base64,${sigData}`}
                      alt="Student Signature"
                    />
                  );
                }

                // Check if it's a file path
                if (typeof sigData === 'string' && (sigData.includes('.png') || sigData.includes('.jpg') || sigData.includes('.jpeg'))) {
                  return (
                    <SignatureImage src={sigData} alt="Student Signature" />
                  );
                }

                // Return name if no valid signature data
                return <SigValue>{student.firstName} {student.lastName}</SigValue>;
              })()}
              <SigLabel style={{ marginLeft: '50px' }}>Print Name:</SigLabel>
              <SigValue style={{minWidth: '220px'}}>{student.firstName} {student.lastName}</SigValue>
              <SigLabel style={{ marginLeft: '20px' }}>Date:</SigLabel>
              <DateValue style={{minWidth: '120px'}}>{dateStr}</DateValue>
            </SignatureRow>

            
          </SignatureSection>

        </FormContent>
      </div>
      <DownloadButton className="print-hide" onClick={handleDownload}>Print Form</DownloadButton>
    </Container>
  );
};

export default CovidVaccinationAttestationForm;
